import {
	connectToRouter,
	createBrowserNavigationUrl,
	Navigation,
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps
} from "client/Navigation";
import {
	FunctionComponent,
	ReactElement,
	useContext,
	useEffect
} from "react";
import { CommunityNodeId } from "../Sidebar/HubSidebar";
import { isNil, isNotNil } from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import { HubDataSource } from "client/Api/Hub/hub";
import { HubLayoutContext } from "../HubLayout";
import { DiscoverPage } from "./DiscoverPage";
import { CommunityFeedPage } from "./CommunityFeedPage";
import { hubRouting } from "../routing";

export type CommunitiesPageProps = {
	id?: CommunityNodeId;
	onNavigate: NavigationCallback;
};

export const CommunitiesPage = (props: CommunitiesPageProps): ReactElement => {
	const {
		id,
		onNavigate
	} = props;

	const {
		changeActiveSidebarItem
	} = useContext(HubLayoutContext);


	useEffect(
		(): void => {
			changeActiveSidebarItem({
				source: HubDataSource.Community,
				communityId: id
			});
		},
		[id, changeActiveSidebarItem]
	);

	if (isNil(id)) {
		return (
			<DiscoverPage onNavigate={onNavigate} />
		);
	}

	return (
		<CommunityFeedPage
			id={id}
			onNavigate={onNavigate}
		/>
	);
};
CommunitiesPage.displayName = "CommunitiesPage";

export const CommunitiesPageForRouter: FunctionComponent = connectToRouter(
	CommunitiesPage,
	{
		"id": {
			isRequired: false,
			tryParse: tryParseCommunityId
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export type CommunitiesPageRouteParams = RouteParametersFromProps<
	CommunitiesPageProps,
	never,
	| "id"
>;

export function tryParseCommunityId(rawValue: string | nil): TryResult<CommunityNodeId> {
	if (isNil(rawValue)) {
		return {
			wasSuccessful: false
		};
	}

	if (rawValue.toUpperCase() === "MY-FEED") {
		return {
			wasSuccessful: true,
			result: "my-feed"
		};
	}

	return tryParseInt(rawValue);
}

export type CommunityRouterUrlOptions = {
	source: HubDataSource.Community;
	communityId?: CommunityNodeId;
	postId?: number;
};

export function getHubNavigationForCommunity(
	options: CommunityRouterUrlOptions
): Navigation {
	if (isNotNil(options.postId)) {
		return hubRouting.communities
			.post({
				queryParams: {
					communityId: isNotNil(options.communityId)
						? `${options.communityId}`
						: undefined
				},
				routeParams: {
					id: `${options.postId}`
				}
			});
	}

	return hubRouting.communities
		.overviews({
			routeParams: {
				id: isNotNil(options.communityId)
					? `${options.communityId}`
					: undefined
			}
		});
}

export function getHubUrlForCommunity(
	options: CommunityRouterUrlOptions
): string {
	return createBrowserNavigationUrl(
		getHubNavigationForCommunity(options)
	);
}
